import { __assign, __awaiter, __generator, __read } from "tslib";
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, mergeClasses, Tooltip, useFluent, } from '@fluentui/react-components';
import { useFragment, useNovaEventing } from '@nova/react';
import { AppBarEvents, LaunchTileEventOriginator, } from '@office-start/appbar-events';
import React, { useMemo, useState } from 'react';
import { ViewMode } from './Constants';
import useAppBarContext from './hooks/useAppBarContext';
import { useLaunchTileStyles } from './LaunchTile.styles';
import { behaviorFragment as LaunchTile_launchTileBehavior, dataFragment as LaunchTile_m365App, } from './LaunchTile.types';
import { TileBadge } from './TileBadge';
import { TileIcon } from './TileIcon';
var useLaunchCallback = function (id, platformType, tileElementID, launchOut, tilePosition, tileHostContext, context) {
    var novaEventing = useNovaEventing();
    var launchCallback = React.useCallback(function (reactEvent) { return __awaiter(void 0, void 0, void 0, function () {
        var launchEvent;
        return __generator(this, function (_a) {
            launchEvent = AppBarEvents.launch(function () {
                return {
                    reactEvent: reactEvent,
                    tileID: id,
                    platformType: platformType,
                    tileElementID: tileElementID,
                    launchOut: launchOut,
                    tilePosition: tilePosition,
                    tileHostContext: tileHostContext,
                    launchContext: context,
                };
            });
            if (reactEvent) {
                novaEventing.bubble({
                    event: launchEvent,
                    reactEvent: reactEvent,
                });
            }
            else {
                novaEventing.generateEvent({
                    event: launchEvent,
                });
            }
            return [2 /*return*/];
        });
    }); }, [
        id,
        platformType,
        tileElementID,
        launchOut,
        tilePosition,
        tileHostContext,
        novaEventing,
        context,
    ]);
    return { launchCallback: launchCallback };
};
var useGetMoreInfoCallback = function (id) {
    var novaEventing = useNovaEventing();
    var moreInfoCallback = React.useCallback(function (reactEvent) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            novaEventing.bubble({
                event: AppBarEvents.moreInfo(function () {
                    return {
                        tileID: id,
                    };
                }),
                reactEvent: reactEvent,
            });
            return [2 /*return*/];
        });
    }); }, [id, novaEventing]);
    return { moreInfoCallback: moreInfoCallback };
};
var useUninstallCallback = function (id, name, isInUse) {
    var novaEventing = useNovaEventing();
    var uninstallCallback = React.useCallback(function (reactEvent) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            novaEventing.bubble({
                event: AppBarEvents.uninstall(function () {
                    return {
                        isInUse: isInUse,
                        tileID: id,
                        appName: name,
                    };
                }),
                reactEvent: reactEvent,
            });
            return [2 /*return*/];
        });
    }); }, [id, name, novaEventing]);
    return { uninstallCallback: uninstallCallback };
};
/**
 * Launch Tile React functional component.
 *
 */
export var LaunchTileRender = function (_a, ref) {
    var _b, _c;
    var index = _a.index, viewMode = _a.viewMode, launchTileBehaviorRef = _a.launchTileBehavior, m365AppRef = _a.m365App, assignShortcutHandler = _a.assignShortcutHandler, unassignShortcutHandler = _a.unassignShortcutHandler, onPinStateChange = _a.onPinStateChange, onClicked = _a.onClicked, onContextMenu = _a.onContextMenu, linkedEntity = _a.linkedEntity, dragDropEnabled = _a.dragDropEnabled, enableMicaV2DesktopStyles = _a.enableMicaV2DesktopStyles, enableMicaV2WebStyles = _a.enableMicaV2WebStyles, backgroundShade = _a.backgroundShade, isListItem = _a.isListItem;
    var _d = useFragment(LaunchTile_m365App, m365AppRef), id = _d.id, platformType = _d.platformType, name = _d.name, accessibleName = _d.accessibleName, icon = _d.icon, outlineIcon = _d.outlineIcon, badge = _d.badge, selectedStateIconOverride = _d.selectedStateIconOverride, isInUse = _d.isInUse, canLaunchOut = _d.canLaunchOut, isPinnable = _d.isPinnable, enablePlatformCommands = _d.enablePlatformCommands, canBeUnacquired = _d.canBeUnacquired, pinnedIndex = _d.pinnedIndex, secondaryActions = _d.secondaryActions, brandingColor = _d.brandingColor;
    var _e = useFragment(LaunchTile_launchTileBehavior, launchTileBehaviorRef), transparent = _e.transparent, pinningEnabled = _e.pinningEnabled, platformAppCommands = _e.platformAppCommands, useHubColorForSelectedTileLabel = _e.useHubColorForSelectedTileLabel, useOutlineIcon = _e.useOutlineIcon;
    var readerBoxId = "readerBox-".concat(id);
    var dir = useFluent().dir;
    var tileStyles = useLaunchTileStyles();
    var _f = __read(useState(false), 2), isHovered = _f[0], setIsHovered = _f[1];
    var buttonRef = React.useRef(null);
    var launchTileContainerRef = React.useRef(null);
    var _g = useSortable({ id: id, disabled: !dragDropEnabled }), listeners = _g.listeners, setNodeRef = _g.setNodeRef, transform = _g.transform, transition = _g.transition, isDragging = _g.isDragging;
    var appBarContext = useAppBarContext();
    var containerStyle = React.useMemo(function () {
        return {
            transform: CSS.Transform.toString(transform),
            transition: transition,
        };
    }, [transform, transition]);
    var badgeClipStyles = dir === 'rtl' ? tileStyles.badgeClipRtl : tileStyles.badgeClip;
    var isCommonButton = !enableMicaV2DesktopStyles &&
        !enableMicaV2WebStyles &&
        viewMode !== ViewMode.Flyout;
    var buttonStyles = useMemo(function () {
        return mergeClasses(tileStyles.button, tileStyles.tileFocusIndicator, isCommonButton && tileStyles.buttonCommon, isCommonButton && backgroundShade === 'ThreeColumnView'
            ? tileStyles.buttonCommonThreeColumnView
            : tileStyles.buttonCommonTwoColumnView, enableMicaV2DesktopStyles && tileStyles.micaButtonV2Desktop, enableMicaV2WebStyles && tileStyles.micaButtonV2Web, viewMode === ViewMode.Wide && tileStyles.wideButton, viewMode === ViewMode.Flyout && tileStyles.flyoutTile, !!transparent && tileStyles.transparentBackground, !!isInUse && tileStyles.selected, !!isInUse &&
            ((enableMicaV2DesktopStyles &&
                tileStyles.micaSelectedV2Desktop) ||
                (enableMicaV2WebStyles && tileStyles.micaSelectedV2Web)), viewMode !== ViewMode.Flyout &&
            (badge === null || badge === void 0 ? void 0 : badge.__typename) === 'M365ApplicationNumericBadge' &&
            !!(badge === null || badge === void 0 ? void 0 : badge.count) &&
            badgeClipStyles, isDragging && tileStyles.dragging);
    }, [
        tileStyles.button,
        tileStyles.tileFocusIndicator,
        tileStyles.buttonCommon,
        tileStyles.buttonCommonThreeColumnView,
        tileStyles.buttonCommonTwoColumnView,
        tileStyles.micaButtonV2Desktop,
        tileStyles.micaButtonV2Web,
        tileStyles.wideButton,
        tileStyles.flyoutTile,
        tileStyles.transparentBackground,
        tileStyles.selected,
        tileStyles.micaSelectedV2Desktop,
        tileStyles.micaSelectedV2Web,
        tileStyles.dragging,
        isCommonButton,
        backgroundShade,
        enableMicaV2DesktopStyles,
        enableMicaV2WebStyles,
        viewMode,
        transparent,
        isInUse,
        badge === null || badge === void 0 ? void 0 : badge.__typename,
        badge === null || badge === void 0 ? void 0 : badge.count,
        badgeClipStyles,
        isDragging,
    ]);
    var enableContextMenu = canLaunchOut ||
        (pinningEnabled && isPinnable) ||
        (enablePlatformCommands && (platformAppCommands === null || platformAppCommands === void 0 ? void 0 : platformAppCommands.aboutCommandEnabled)) ||
        (enablePlatformCommands &&
            canBeUnacquired &&
            (platformAppCommands === null || platformAppCommands === void 0 ? void 0 : platformAppCommands.uninstallCommandEnabled)) ||
        (secondaryActions && secondaryActions.length > 0);
    var launchOutCallback = useLaunchCallback(id, platformType !== null && platformType !== void 0 ? platformType : undefined, (_b = buttonRef.current) === null || _b === void 0 ? void 0 : _b.id, true, index, viewMode === ViewMode.Flyout ? 'Flyout' : 'AppBar', linkedEntity).launchCallback;
    var launchInlineCallback = useLaunchCallback(id, platformType !== null && platformType !== void 0 ? platformType : undefined, (_c = buttonRef.current) === null || _c === void 0 ? void 0 : _c.id, false, index, viewMode === ViewMode.Flyout ? 'Flyout' : 'AppBar', linkedEntity).launchCallback;
    var moreInfoCallback = useGetMoreInfoCallback(id).moreInfoCallback;
    var uninstallCallback = useUninstallCallback(id, name, !!isInUse).uninstallCallback;
    var novaEventing = useNovaEventing();
    var useCustomActionCallback = React.useCallback(function (reactEvent, eventType, eventData) {
        var novaEvent = {
            originator: LaunchTileEventOriginator,
            type: eventType,
            data: function () { return eventData; },
        };
        novaEventing.bubble({
            event: novaEvent,
            reactEvent: reactEvent,
        });
    }, [novaEventing]);
    /**
     * Tile click handler
     *
     * @param {React.MouseEvent<HTMLElement>} [ev] - Click event from the tile
     */
    var useTileClickCallback = React.useCallback(function (ev) {
        if (ev) {
            if (ev.ctrlKey || ev.shiftKey || ev.metaKey) {
                // The control/shift/command key was pressed while clicking the tile, so launch out
                launchOutCallback(ev);
            }
            else {
                launchInlineCallback(ev);
                if (onClicked && !ev.defaultPrevented) {
                    onClicked();
                }
            }
            ev.stopPropagation();
        }
    }, [launchOutCallback, launchInlineCallback, onClicked]);
    var useTileAuxClickCallback = React.useCallback(function (ev) {
        if (ev) {
            if (ev.button === 1) {
                // Middle mouse button should launch out
                launchOutCallback(ev);
            }
            ev.stopPropagation();
        }
    }, [launchOutCallback]);
    var useTileContainerClickCallback = React.useCallback(function (_ev) {
        if (buttonRef && buttonRef.current) {
            // Let the app tile container handle the click from the badge
            // and proxy the click and focus to the underlying button
            buttonRef.current.focus();
            buttonRef.current.click();
        }
    }, [buttonRef]);
    var useShortcutCallback = React.useCallback(function () {
        launchInlineCallback();
    }, [launchInlineCallback]);
    // Add/Remove shortcut handlers
    React.useEffect(function () {
        // overflow index needs to be calculated before we assign shortcut handlers to the launch tiles
        // otherwise we risk having the same index for multiple tiles.
        if (assignShortcutHandler &&
            appBarContext.overflowIndex !== undefined &&
            index < appBarContext.overflowIndex) {
            assignShortcutHandler(index + 1, useShortcutCallback);
        }
        return function () {
            if (unassignShortcutHandler) {
                unassignShortcutHandler(index + 1, useShortcutCallback);
            }
        };
    }, [
        appBarContext.overflowIndex,
        assignShortcutHandler,
        index,
        unassignShortcutHandler,
        useShortcutCallback,
    ]);
    var contextualCommands = React.useMemo(function () {
        return {
            onLaunchOut: canLaunchOut ? launchOutCallback : undefined,
            onGetMoreInfo: enablePlatformCommands &&
                (platformAppCommands === null || platformAppCommands === void 0 ? void 0 : platformAppCommands.aboutCommandEnabled)
                ? moreInfoCallback
                : undefined,
            onPinStateChange: pinningEnabled && isPinnable ? onPinStateChange : undefined,
            onUninstall: enablePlatformCommands &&
                canBeUnacquired &&
                (platformAppCommands === null || platformAppCommands === void 0 ? void 0 : platformAppCommands.uninstallCommandEnabled)
                ? uninstallCallback
                : undefined,
            onCustomAction: useCustomActionCallback,
            customActions: secondaryActions,
        };
    }, [
        canLaunchOut,
        secondaryActions,
        isPinnable,
        enablePlatformCommands,
        launchOutCallback,
        moreInfoCallback,
        onPinStateChange,
        pinningEnabled,
        canBeUnacquired,
        platformAppCommands,
        useCustomActionCallback,
        uninstallCallback,
    ]);
    var onTileContextMenu = React.useCallback(function (evt) {
        if (!enableContextMenu) {
            evt.preventDefault();
            return;
        }
        evt.preventDefault();
        if (onContextMenu) {
            onContextMenu(evt.currentTarget, pinnedIndex !== null && pinnedIndex !== void 0 ? pinnedIndex : undefined, contextualCommands);
        }
    }, [contextualCommands, enableContextMenu, onContextMenu, pinnedIndex]);
    var onTileMouseOver = React.useCallback(function () {
        setIsHovered(true);
    }, []);
    var onTileMouseLeave = React.useCallback(function () {
        setIsHovered(false);
    }, []);
    var buttonBehavior = useMemo(function () {
        return {
            onMouseOver: onTileMouseOver,
            onMouseLeave: onTileMouseLeave,
            onContextMenu: onTileContextMenu,
        };
    }, [onTileContextMenu, onTileMouseLeave, onTileMouseOver]);
    var tileIcon = useMemo(function () {
        var iconToUse;
        if (useOutlineIcon && outlineIcon && viewMode !== ViewMode.Flyout) {
            iconToUse = outlineIcon;
        }
        else {
            iconToUse =
                isInUse || isHovered ? selectedStateIconOverride !== null && selectedStateIconOverride !== void 0 ? selectedStateIconOverride : icon : icon;
        }
        return iconToUse;
    }, [
        icon,
        isHovered,
        isInUse,
        selectedStateIconOverride,
        useOutlineIcon,
        outlineIcon,
        viewMode,
    ]);
    React.useEffect(function () {
        if (launchTileContainerRef.current) {
            setNodeRef(launchTileContainerRef.current);
        }
    }, [launchTileContainerRef, setNodeRef]);
    var tileButton = useMemo(function () {
        return (React.createElement(Button, __assign({ id: id, "data-tid": id, ref: buttonRef, "aria-label": accessibleName || undefined, "aria-pressed": viewMode !== ViewMode.Flyout && !isListItem
                ? !!isInUse
                : undefined, "aria-describedby": readerBoxId, role: isListItem ? 'listitem' : 'button', "data-clicklog": "custom", icon: React.createElement(TileIcon, { id: id, icon: tileIcon, viewMode: viewMode, label: name, isSelected: isInUse || isHovered, brandingColor: brandingColor, useHubColorForSelectedTileLabel: !!useHubColorForSelectedTileLabel, useOutlineIconFilter: !!useOutlineIcon &&
                    outlineIcon != null &&
                    viewMode !== ViewMode.Flyout, showDotIndicator: platformType === 'm365_messageextension', useMicaStyles: enableMicaV2DesktopStyles || enableMicaV2WebStyles }), className: buttonStyles, onClick: useTileClickCallback, onAuxClick: useTileAuxClickCallback }, buttonBehavior)));
    }, [
        id,
        accessibleName,
        readerBoxId,
        viewMode,
        isListItem,
        isInUse,
        tileIcon,
        name,
        isHovered,
        brandingColor,
        useHubColorForSelectedTileLabel,
        useOutlineIcon,
        outlineIcon,
        platformType,
        enableMicaV2DesktopStyles,
        enableMicaV2WebStyles,
        buttonStyles,
        useTileClickCallback,
        useTileAuxClickCallback,
        buttonBehavior,
    ]);
    React.useImperativeHandle(ref, function () { return ({
        focus: function () {
            if (buttonRef === null || buttonRef === void 0 ? void 0 : buttonRef.current) {
                buttonRef.current.focus();
            }
        },
        getAppIconPositionRect: function () {
            var _a;
            return (_a = buttonRef === null || buttonRef === void 0 ? void 0 : buttonRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
        },
        getAppIconElementRef: function () {
            return buttonRef;
        },
    }); }, [buttonRef]);
    var badgeControl = useMemo(function () {
        return (badge && (React.createElement(TileBadge, { badge: badge, readerBoxId: readerBoxId, transparentBorder: !!transparent, viewMode: viewMode, isDragging: isDragging })));
    }, [badge, isDragging, readerBoxId, transparent, viewMode]);
    return (
    // Note: the tile container needs to act like the tile button
    // so it can properly handle events bubbled up from the badge
    React.createElement(Tooltip, { content: accessibleName !== null && accessibleName !== void 0 ? accessibleName : '', positioning: viewMode === ViewMode.Flyout ? 'above' : 'after', relationship: "label" },
        React.createElement("div", __assign({ className: viewMode === ViewMode.Flyout
                ? tileStyles.flyoutTileContainer
                : tileStyles.tileContainer, onClick: useTileContainerClickCallback, role: "none" }, buttonBehavior, listeners, { ref: launchTileContainerRef, style: containerStyle }),
            tileButton,
            badge && badgeControl)));
};
LaunchTileRender.displayName = 'LaunchTile';
export var LaunchTile = React.forwardRef(LaunchTileRender);
//# sourceMappingURL=LaunchTile.js.map